import { useCallback } from "react";

import CopyFieldDropdownMenuItem from "components/CopyField/DropdownMenuItem";
import DropdownMenuEllipsis, {
  DropdownMenuEllipsisProps,
} from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import { TrackAnalyticsEventProperties } from "shared/Analytics";
import useCanManageAiIntegration from "shared/AiIntegration/access/useCanManageAiIntegration";

import { AiIntegrationView } from "../../useSearchAiIntegrations/gql";
import { showAiIntegrationDeleteConfirmation } from "../DeleteConfirmation";
import { showAiIntegrationDetailsDrawer } from "../DetailsDrawer";
import { showAiIntegrationFormDrawer } from "../FormDrawer";

type AiIntegrationsActionsDropdownProps = {
  integration: AiIntegrationView;
  trackAnalytics: (event: string, props?: TrackAnalyticsEventProperties) => void;
  hideSeeDetails?: boolean;
  onDeleteSuccess?: () => void;
  dotsSize?: DropdownMenuEllipsisProps["dotsSize"];
  refetchQueriesOnEdit: string[];
  refetchQueriesOnDelete?: string[];
};

const AiIntegrationsActionsDropdown = ({
  integration,
  trackAnalytics,
  hideSeeDetails,
  onDeleteSuccess,
  dotsSize = "small",
  refetchQueriesOnEdit,
  refetchQueriesOnDelete,
}: AiIntegrationsActionsDropdownProps) => {
  const canManageAiIntegration = useCanManageAiIntegration();

  const handleEditIntegration = useCallback(async () => {
    showAiIntegrationFormDrawer({
      integration,
      trackAnalytics,
      refetchQueriesOnEdit,
    });
  }, [integration, refetchQueriesOnEdit, trackAnalytics]);

  const handleDeleteIntegration = useCallback(() => {
    showAiIntegrationDeleteConfirmation({
      integration,
      onDeleteSuccess,
      refetchQueriesOnDelete,
    });
  }, [integration, onDeleteSuccess, refetchQueriesOnDelete]);

  const handleSeeDetails = useCallback(() => {
    showAiIntegrationDetailsDrawer({ integration, trackAnalytics });
  }, [integration, trackAnalytics]);

  const hasAccessToManageIntegration = canManageAiIntegration(integration);

  return (
    <DropdownMenuEllipsis tooltip="Integration actions" dotsSize={dotsSize}>
      <CopyFieldDropdownMenuItem
        title="Copy ID"
        value={integration.id}
        onCopy={() => trackAnalytics("Copy ID clicked")}
      />
      {!hideSeeDetails && (
        <DropdownMenuItem onAction={handleSeeDetails}>See details</DropdownMenuItem>
      )}
      <DropdownMenuItem
        onAction={handleEditIntegration}
        tooltip={
          !hasAccessToManageIntegration
            ? "You don't have access to manage this integration"
            : undefined
        }
        isTooltipActive={!hasAccessToManageIntegration}
        isDisabled={!hasAccessToManageIntegration}
        tooltipPlacement="left"
        tooltipWidthMode="maxWidthSm"
      >
        Edit
      </DropdownMenuItem>
      <DropdownMenuItem
        onAction={handleDeleteIntegration}
        danger
        isDisabled={!hasAccessToManageIntegration}
        tooltip={
          !hasAccessToManageIntegration
            ? "You don't have access to manage this integration"
            : undefined
        }
        isTooltipActive={!hasAccessToManageIntegration}
        tooltipPlacement="left"
        tooltipWidthMode="maxWidthSm"
      >
        Delete
      </DropdownMenuItem>
    </DropdownMenuEllipsis>
  );
};

export default AiIntegrationsActionsDropdown;
